import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Send, Shield, Ban, Clock } from "lucide-react";
import { MessageModMenu } from "@/components/ChatModeration";
import { toast } from "sonner";
import { UsernamePrompt } from "@/components/UsernamePrompt";

function timeLabel(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
      : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function ChatPage() {
  const { user, refresh } = useAuth();
  const { t } = useSettings();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const bannedRef = useRef(false);
  const [now, setNow] = useState(() => Date.now());
  const muted = user?.muted_until && new Date(user.muted_until).getTime() > now;

  useEffect(() => {
    bannedRef.current = !!user?.banned;
  }, [user?.banned]);

  useEffect(() => {
    if (!user?.muted_until) return;
    const id = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(id);
  }, [user?.muted_until]);

  const load = async (after) => {
    try {
      const { data } = await api.get("/chat/messages", { params: after ? { after } : {} });
      if (after) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.message_id));
          const fresh = data.filter((m) => !seen.has(m.message_id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      } else {
        setMessages(data);
      }
    } catch (err) {
      if (err?.response?.status === 403 && !bannedRef.current) {
        bannedRef.current = true;
        setMessages([]);
        refresh();
      }
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      const last = messagesRef.current[messagesRef.current.length - 1];
      load(last?.created_at);
    }, 2500);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
    const el = containerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    if (nearBottom) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const content = text.trim();
    if (!content) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", { content });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send message");
      if (err?.response?.status === 403) refresh();
    } finally {
      setSending(false);
    }
  };

  const removeMsg = async (m) => {
    try {
      await api.delete(`/chat/messages/${m.message_id}`);
      setMessages((list) => list.filter((x) => x.message_id !== m.message_id));
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] md:h-[calc(100vh-4rem)]">
      <div className="border-b border-zinc-800/60 px-4 sm:px-8 py-4 bg-black/40 backdrop-blur">
        <div className="max-w-4xl mx-auto">
          <div className="text-[10px] tracking-[0.5em] uppercase text-crimson-bright">{t("chat.eyebrow")}</div>
          <h1 className="font-display font-bold tracking-[0.15em] uppercase text-xl sm:text-2xl text-white">{t("chat.title")}</h1>
        </div>
      </div>

      <div ref={containerRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 pb-28 md:pb-6" data-testid="chat-scroll">
        <div className="max-w-4xl mx-auto space-y-3">
          {messages.length === 0 && !user?.banned && (
            <div className="text-center text-zinc-500 py-20 text-sm">
              {t("chat.empty")}
            </div>
          )}
          {messages.map((m) => {
            const mine = m.user_id === user?.user_id;
            const isAdmin = m.role === "admin";
            return (
              <div
                key={m.message_id}
                data-testid={`chat-message-${m.message_id}`}
                className={`flex gap-3 ${mine ? "flex-row-reverse" : "flex-row"}`}
              >
                <div className={`max-w-[80%] sm:max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  <div className={`flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase mb-1 ${mine ? "flex-row-reverse" : ""}`}>
                    <span className={`normal-case tracking-wide text-xs font-semibold ${isAdmin ? "text-crimson-bright" : "text-silver"}`} data-testid={`chat-author-${m.message_id}`}>
                      {m.author_name}
                    </span>
                    {isAdmin && <Shield size={10} className="text-crimson-bright" />}
                    <span className="text-zinc-600">· {timeLabel(m.created_at)}</span>
                  </div>
                  <div
                    className={`px-4 py-2.5 rounded-sm border text-sm leading-relaxed break-words ${
                      mine
                        ? "bg-crimson/15 border-crimson/60 text-white"
                        : "bg-zinc-900/70 border-zinc-700 text-zinc-100"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
                {user?.role === "admin" && (
                  <MessageModMenu
                    message={m}
                    onDelete={removeMsg}
                    onBanned={(uid) => setMessages((list) => list.filter((x) => x.user_id !== uid))}
                  />
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {user?.banned ? (
        <ChatBlockedNotice testid="chat-banned-notice" text="You have been banned from Tavern Chat." />
      ) : muted ? (
        <ChatBlockedNotice icon={Clock} testid="chat-timeout-notice" text={`You are timed out until ${new Date(user.muted_until).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.`} />
      ) : !user?.username ? <UsernamePrompt /> : (
      <form onSubmit={send} className="fixed md:static bottom-14 md:bottom-0 inset-x-0 border-t border-zinc-800/60 bg-black/85 backdrop-blur px-4 sm:px-8 py-3 z-20" data-testid="chat-form">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={500}
            placeholder={t("chat.placeholder")}
            className="tavern-input flex-1 px-4 py-2.5 rounded-sm text-sm"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="btn-crimson px-4 py-2.5 text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="chat-send"
          >
            <Send size={14} /> Send
          </button>
        </div>
      </form>
      )}
    </div>
  );
}

function ChatBlockedNotice({ text, testid, icon: Icon = Ban }) {
  return (
    <div className="fixed md:static bottom-14 md:bottom-0 inset-x-0 border-t border-crimson/40 bg-black/90 backdrop-blur px-4 sm:px-8 py-4 z-20" data-testid={testid}>
      <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm text-zinc-300">
        <Icon size={16} className="text-crimson-bright flex-shrink-0" /> {text}
      </div>
    </div>
  );
}
